<script setup lang="ts">
    import { onMounted, reactive } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import { getAdministrators, addAdministrator, removeAdministrator, Administrator } from './Administrators.access';

    const state = reactive<{
        administrators: Administrator[];
        loading: boolean;
        emailToAdd: string;
    }>({
        administrators: [],
        loading: false,
        emailToAdd: '',
    });

    onMounted(async () => {
        state.loading = true;
        try {
            state.administrators = await getAdministrators();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load administrators' });
        } finally {
            state.loading = false;
        }
    });

    async function addUser(): Promise<void> {
        if (!state.emailToAdd) return;
        try {
            const user = await addAdministrator(state.emailToAdd);
            state.administrators.push(user);
            state.emailToAdd = '';
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Added user as BackOfficeAdministrator' });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Failed', content: 'Could not add user' });
        }
    }

    async function removeUser(userId: string): Promise<void> {
        try {
            await removeAdministrator(userId);
            state.administrators = state.administrators.filter((a) => a.UserId !== userId);
            bsAlert({ type: AlertTypes.Success, title: 'Success', content: 'Removed user from BackOfficeAdministrator role' });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Failed', content: 'Could not remove user' });
        }
    }
</script>

<template>
    <div class="max-700">
        <h2>Manage Back Office Administrators</h2>
        <p class="c-danger strong">Users added here will get superuser access to all customers!</p>

        <form autocomplete="off" @submit.prevent="addUser">
            <div class="search-group form-group">
                <label for="email-to-add">Add user by email:</label>
                <div class="input-group">
                    <span class="input-group-addon">@</span>
                    <input
                        id="email-to-add"
                        v-model="state.emailToAdd"
                        type="text"
                        class="form-control"
                        placeholder="Enter email address here" />
                    <span class="input-group-btn">
                        <button type="submit" class="btn btn-primary">Add</button>
                    </span>
                </div>
            </div>
        </form>

        <h3>All Back Office Administrators</h3>

        <div v-if="state.loading">Loading...</div>
        <table v-else class="table table-hover table-sticky-headers">
            <thead>
                <tr>
                    <th>Email address</th>
                    <th class="text-right">Remove access</th>
                </tr>
            </thead>
            <tbody class="td-align-middle">
                <tr v-for="admin in state.administrators" :key="admin.UserId">
                    <td>{{ admin.Email }}</td>
                    <td class="text-right">
                        <button class="btn btn-warning btn-xs" type="button" @click="removeUser(admin.UserId)">
                            Remove
                        </button>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script lang="ts">
    export default {
        name: 'Administrators',
    };
</script>

<style></style>
